// src/services/ocrService.js
import axios from "axios";
import { GEMINI_API_KEY } from "../utils/envConfig";

const GEMINI_API_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent";
const API_KEY = GEMINI_API_KEY;

// Helper function to extract retry delay from error response
const extractRetryDelay = (error) => {
  try {
    if (error.response?.data?.error?.details) {
      for (const detail of error.response.data.error.details) {
        if (detail['@type'] === 'type.googleapis.com/google.rpc.RetryInfo' && detail.retryDelay) {
          // Parse delay like "2s" or "2.896450551s"
          const delayStr = detail.retryDelay.replace('s', '');
          const delayMs = parseFloat(delayStr) * 1000;
          return Math.min(delayMs, 60000); // Cap at 60 seconds
        }
      }
    }
  } catch (e) {
    console.warn("Error extracting retry delay:", e);
  }
  return null;
};

// Helper function to sleep/delay
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Retry logic with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000, onProgress = null) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Check if it's a rate limit error (429)
      const isRateLimitError = error.response?.status === 429 || 
                               error.response?.data?.error?.code === 429 ||
                               error.response?.data?.error?.status === 'RESOURCE_EXHAUSTED';
      
      if (isRateLimitError && attempt < maxRetries) {
        // Extract retry delay from error response, or use exponential backoff
        let retryDelay = extractRetryDelay(error);
        
        if (!retryDelay) {
          // Exponential backoff: baseDelay * 2^attempt, with jitter
          retryDelay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
        }
        
        // Cap retry delay at 60 seconds
        retryDelay = Math.min(retryDelay, 60000);
        const retrySeconds = Math.ceil(retryDelay / 1000);
        
        // Notify progress callback about retry
        if (onProgress) {
          onProgress({ 
            message: `Rate limit exceeded. Retrying in ${retrySeconds} second${retrySeconds !== 1 ? 's' : ''}... (attempt ${attempt + 1}/${maxRetries + 1})`,
            retrying: true,
            retryDelay: retryDelay,
            attempt: attempt + 1,
            maxRetries: maxRetries + 1
          });
        }
        
        console.warn(`Rate limit exceeded. Retrying in ${retrySeconds}s (attempt ${attempt + 1}/${maxRetries + 1})...`);
        
        // Show countdown if progress callback is available
        if (onProgress) {
          for (let remaining = retrySeconds; remaining > 0; remaining--) {
            await sleep(1000);
            if (onProgress) {
              onProgress({ 
                message: `Rate limit exceeded. Retrying in ${remaining} second${remaining !== 1 ? 's' : ''}... (attempt ${attempt + 1}/${maxRetries + 1})`,
                retrying: true,
                retryDelay: remaining * 1000,
                attempt: attempt + 1,
                maxRetries: maxRetries + 1
              });
            }
          }
        } else {
          await sleep(retryDelay);
        }
        
        continue;
      }
      
      // If not a rate limit error, or max retries reached, throw the error
      throw error;
    }
  }
  
  throw lastError;
};

export const processInvoiceWithOCR = async (base64Image, fileType = 'image/jpeg', onProgress = null) => {
  try {
    if (!API_KEY) {
      throw new Error("Gemini API key not configured");
    }

    const prompt = `
    Analyze this medicine invoice image and extract the following information in JSON format:
    
    {
      "invoiceNumber": "invoice number if visible",
      "invoiceDate": "date in YYYY-MM-DD format",
      "supplier": "supplier/company name",
      "supplierContact": "supplier phone/email if visible",
      "supplierAddress": "supplier address if visible",
      "totalAmount": "total amount as number",
      "medicines": [
        {
          "name": "medicine name",
          "batchNumber": "batch number if visible",
          "quantity": "quantity as number",
          "expiryDate": "expiry date in YYYY-MM-DD format if visible",
          "manufacturer": "manufacturer name if visible",
          "costPerUnit": "cost per unit as number",
          "totalCost": "total cost for this medicine as number"
        }
      ]
    }
    
    Rules:
    1. Extract only medicine-related items
    2. If any field is not visible, use null
    3. Ensure all numbers are properly formatted
    4. Medicine names should be clear and complete
    5. Extract supplier contact (phone/email) and address from invoice header
    6. Return only valid JSON, no additional text
    `;

    const requestBody = {
      contents: [
        {
          parts: [
            {
              text: prompt
            },
            {
              inline_data: {
                mime_type: fileType,
                data: base64Image.split(',')[1] // Remove data:image/jpeg;base64, prefix
              }
            }
          ]
        }
      ],
      generationConfig: {
        temperature: 0.1,
        topK: 32,
        topP: 1,
        maxOutputTokens: 2048,
      }
    };

    // Use retry logic with exponential backoff for rate limit errors
    const response = await retryWithBackoff(async () => {
      if (onProgress) {
        onProgress({ message: "Sending request to Gemini API...", retrying: false });
      }
      
      return await axios.post(
        `${GEMINI_API_URL}?key=${API_KEY}`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    }, 3, 2000, onProgress); // Max 3 retries, base delay 2 seconds, pass progress callback

    if (response.data && response.data.candidates && response.data.candidates[0]) {
      if (onProgress) {
        onProgress({ message: "Processing OCR response..." });
      }
      
      const extractedText = response.data.candidates[0].content.parts[0].text;
      
      // Clean and parse the JSON response
      const cleanedText = extractedText.replace(/```json\n?|\n?```/g, '').trim();
      const parsedData = JSON.parse(cleanedText);
      
      // Validate and clean the data
      return validateAndCleanOCRData(parsedData);
    } else {
      throw new Error("No data extracted from the image");
    }

  } catch (error) {
    console.error("OCR processing error:", error);
    
    // Handle rate limit errors with user-friendly message
    if (error.response?.status === 429 || 
        error.response?.data?.error?.code === 429 ||
        error.response?.data?.error?.status === 'RESOURCE_EXHAUSTED') {
      const quotaInfo = error.response?.data?.error?.details?.find(
        d => d['@type'] === 'type.googleapis.com/google.rpc.QuotaFailure'
      );
      
      // Check if it's a daily quota (which won't reset until next day)
      const isDailyQuotaExceeded = quotaInfo?.violations?.some(v => 
        v.quotaId?.includes("PerDay") || v.quotaId?.includes("Daily")
      );
      
      let userMessage = "Gemini API rate limit exceeded. ";
      
      if (quotaInfo?.violations) {
        // Extract unique quota types to avoid duplicates
        const quotaTypes = new Set();
        quotaInfo.violations.forEach(v => {
          if (v.quotaId?.includes("PerDay") || v.quotaId?.includes("Daily")) {
            quotaTypes.add("daily quota");
          } else if (v.quotaId?.includes("PerMinute")) {
            quotaTypes.add("per-minute quota");
          } else if (v.quotaId?.includes("InputTokens")) {
            quotaTypes.add("input token quota");
          } else {
            quotaTypes.add("quota limit");
          }
        });
        
        const quotaTypesArray = Array.from(quotaTypes);
        if (quotaTypesArray.length > 0) {
          userMessage += `You have exceeded your ${quotaTypesArray.join(" and ")}. `;
        }
      }
      
      const retryDelay = extractRetryDelay(error);
      if (retryDelay) {
        const retrySeconds = Math.ceil(retryDelay / 1000);
        userMessage += `Please wait ${retrySeconds} second${retrySeconds !== 1 ? 's' : ''} before trying again`;
        
        if (isDailyQuotaExceeded) {
          userMessage += ", or try again tomorrow when your daily quota resets";
        }
        
        userMessage += ", or check your Gemini API billing/plan.";
      } else {
        if (isDailyQuotaExceeded) {
          userMessage += "Your daily quota has been exhausted. Please try again tomorrow or upgrade your Gemini API plan.";
        } else {
          userMessage += "Please try again later or check your Gemini API billing/plan.";
        }
      }
      
      // Create a custom error with a flag to indicate if mock data should be used
      const customError = new Error(userMessage);
      customError.isQuotaExhausted = true;
      customError.isDailyQuota = isDailyQuotaExceeded;
      customError.retryDelay = retryDelay;
      throw customError;
    }
    
    // Return mock data for development/testing (only for non-rate-limit errors)
    if (error.message.includes("API key not configured") || 
        (error.message.includes("Network Error") && !error.response)) {
      console.warn("Using mock data for development");
      return getMockOCRData();
    }
    
    // Re-throw with original or enhanced error message
    const errorMessage = error.response?.data?.error?.message || 
                        error.message || 
                        "Unknown error occurred";
    throw new Error(`OCR processing failed: ${errorMessage}`);
  }
};

const validateAndCleanOCRData = (data) => {
  // Ensure medicines array exists
  if (!data.medicines || !Array.isArray(data.medicines)) {
    data.medicines = [];
  }

  // Clean and validate each medicine
  data.medicines = data.medicines.map(medicine => ({
    name: medicine.name || "Unknown Medicine",
    batchNumber: medicine.batchNumber || null,
    quantity: parseInt(medicine.quantity) || 0,
    expiryDate: medicine.expiryDate || null,
    manufacturer: medicine.manufacturer || null,
    costPerUnit: parseFloat(medicine.costPerUnit) || 0,
    totalCost: parseFloat(medicine.totalCost) || (parseInt(medicine.quantity) || 0) * (parseFloat(medicine.costPerUnit) || 0)
  }));

  // Clean other fields
  return {
    invoiceNumber: data.invoiceNumber || null,
    invoiceDate: data.invoiceDate || null,
    supplier: data.supplier || null,
    supplierContact: data.supplierContact || null,
    supplierAddress: data.supplierAddress || null,
    totalAmount: parseFloat(data.totalAmount) || 0,
    medicines: data.medicines
  };
};

const getMockOCRData = () => {
  return {
    invoiceNumber: "INV-2024-001",
    invoiceDate: "2024-01-15",
    supplier: "Medical Supplies Ltd",
    supplierContact: "9876543210",
    supplierAddress: "123 Medical Street, Mumbai, Maharashtra",
    totalAmount: 500.00,
    medicines: [
      {
        name: "Paracetamol 500mg",
        batchNumber: "BATCH001",
        quantity: 100,
        expiryDate: "2025-12-31",
        manufacturer: "ABC Pharma",
        costPerUnit: 2.50,
        totalCost: 250.00
      },
      {
        name: "Amoxicillin 250mg",
        batchNumber: "BATCH002",
        quantity: 50,
        expiryDate: "2025-11-30",
        manufacturer: "XYZ Pharma",
        costPerUnit: 5.00,
        totalCost: 250.00
      }
    ]
  };
};

export const addOCRStockToInventory = async (ocrData, user = null) => {
  try {
    // Import services dynamically to avoid circular dependency
    const apiClient = (await import("./apiClient")).default;
    const { getAllMedicine, createNewMedicine } = await import("./medicineNewService");
    
    // Step 1: Fetch all medicines from base raw medicine table
    console.log(`[addOCRStockToInventory] Fetching medicines from base raw medicine table...`);
    const existingMedicines = await getAllMedicine();
    const medicineMap = new Map();
    
    // Create a map of existing medicines by name (case-insensitive)
    if (Array.isArray(existingMedicines)) {
      existingMedicines.forEach(med => {
        const name = (med.medicineName || med.name || '').toLowerCase().trim();
        if (name) {
          medicineMap.set(name, med);
        }
      });
    }
    
    console.log(`[addOCRStockToInventory] Found ${medicineMap.size} existing medicines in base table`);
    
    // Step 2: Check each medicine from OCR data and create in base table if not exists
    const newMedicinesCreated = [];
    const processedMedicines = await Promise.all(
      ocrData.medicines.map(async (medicine) => {
        const medicineName = (medicine.name || '').toLowerCase().trim();
        let existingMedicine = medicineMap.get(medicineName);
        
        // If medicine doesn't exist in base table, create it
        if (!existingMedicine) {
          console.log(`[addOCRStockToInventory] Medicine "${medicine.name}" not found in base table, creating...`);
          
          try {
            const newMedicineData = {
              medicineName: medicine.name,
              medicineType: medicine.medicineType || "Homeopathy",
              manufacturer: medicine.manufacturer || "Unknown",
              supplier: ocrData.supplier || "Unknown",
              medicineDetails: "tablet", // Default, can be updated later
              details: 1, // Default details
              price: medicine.costPerUnit || 0,
              costPerUnit: medicine.costPerUnit || 0,
              userId: user?.id || 1,
            };
            
            const createdMedicine = await createNewMedicine(newMedicineData);
            console.log(`[addOCRStockToInventory] ✅ Created medicine "${medicine.name}" in base table with ID: ${createdMedicine.id}`);
            
            existingMedicine = createdMedicine;
            newMedicinesCreated.push({
              medicine: medicine.name,
              id: createdMedicine.id
            });
            
            // Add to map for potential future lookups in the same batch
            medicineMap.set(medicineName, createdMedicine);
          } catch (createError) {
            console.error(`[addOCRStockToInventory] ❌ Error creating medicine "${medicine.name}":`, createError);
            // Continue with the medicine even if creation failed - backend might handle it
            // But log the error for debugging
          }
        } else {
          console.log(`[addOCRStockToInventory] Medicine "${medicine.name}" already exists in base table (ID: ${existingMedicine.id})`);
        }
        
        // Format batch number as medicineName_batchNumber
        let formattedBatchNumber = null;
        // Clean medicine name: remove special characters, replace spaces with underscores
        const cleanMedicineName = (medicine.name || '').trim().replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
        
        if (medicine.batchNumber) {
          // If batch number already contains medicine name, use as is; otherwise prefix with medicine name
          const batchStr = String(medicine.batchNumber).trim();
          if (batchStr.startsWith(`${cleanMedicineName}_`)) {
            formattedBatchNumber = batchStr;
          } else {
            // Extract numeric part if it exists, otherwise use the batch number as is
            const numericPart = batchStr.replace(/\D/g, '');
            if (numericPart) {
              formattedBatchNumber = `${cleanMedicineName}_${numericPart.padStart(3, '0')}`;
            } else {
              formattedBatchNumber = `${cleanMedicineName}_${batchStr}`;
            }
          }
        } else {
          // If no batch number from OCR, generate default one: medicineName_001
          formattedBatchNumber = `${cleanMedicineName}_001`;
        }
        
        // Return medicine data with the base table medicine ID
        return {
          name: medicine.name,
          batchNumber: formattedBatchNumber,
          quantity: medicine.quantity || 0,
          expiryDate: medicine.expiryDate || null,
          manufacturer: medicine.manufacturer || "Unknown",
          costPerUnit: medicine.costPerUnit || 0,
          totalCost: (medicine.quantity || 0) * (medicine.costPerUnit || 0),
          medicineType: medicine.medicineType || "Homeopathy",
          // Include the medicine ID from base table if available
          baseMedicineId: existingMedicine?.id || null
        };
      })
    );
    
    // Step 3: Prepare the data for the backend endpoint
    const requestData = {
      invoiceNumber: ocrData.invoiceNumber || `OCR-${Date.now()}`,
      invoiceDate: ocrData.invoiceDate || new Date().toISOString().split('T')[0],
      supplierName: ocrData.supplier || "Unknown",
      supplierContact: ocrData.supplierContact || null,
      supplierAddress: ocrData.supplierAddress || null,
      totalAmount: ocrData.totalAmount || 0,
      createdBy: user?.id || 1,
      medicines: processedMedicines
    };

    // Step 4: Use apiClient to automatically add organizationId via interceptor
    console.log(`[addOCRStockToInventory] Sending data to backend endpoint /invoice-purchases...`);
    const response = await apiClient.post('/invoice-purchases', requestData);
    const data = response.data;
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to add OCR stock');
    }

    // Step 5: Return success response in the format expected by OCRResultDialog
    return {
      success: true,
      message: data.message || 'Successfully added medicines to inventory',
      invoicePurchaseId: data.data?.id,
      supplierId: data.data?.supplierId,
      totalAmount: data.data?.totalAmount,
      // Combine new medicines created in frontend with any created by backend
      newMedicinesCreated: [
        ...newMedicinesCreated,
        ...(data.data?.newMedicinesCreated || [])
      ]
    };

  } catch (error) {
    console.error("Error adding OCR stock to inventory:", error);
    throw new Error(`Failed to add stock to inventory: ${error.message}`);
  }
};
